import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faSignOutAlt,
  faQuestionCircle,
  faChevronDown,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import NotificationBell from "../notifications/NotificationBell";
import SearchBar from "../search/SearchBar";
import HealthIndicator from "./HealthIndicator";
import UserSearchModal from "../search/UserSearchModal";

const Header = ({ user }) => {
  const { logout } = useAuth0();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  const handleLogout = () => {
    const returnUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
    logout({
      logoutParams: { returnTo: returnUrl }, // <-- key change
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const isActive = (path) => {
    if (path === "/leagues") {
      return (
        location.pathname === "/leagues" ||
        location.pathname.startsWith("/league/")
      );
    }
    if (path === "/events") {
      return location.pathname.startsWith("/events");
    }
    if (path === "/globalcompetition") {
      return (
        location.pathname === "/globalcompetition" ||
        location.pathname === "/league/events" ||
        location.pathname === "/leaderboard"
      );
    }
    return location.pathname === path;
  };

  return (
    <header className="fixed w-full top-0 z-50 h-16 bg-dark-grey shadow-lg">
      <div className="container mx-auto px-3 max-w-7xl h-full">
        <div className="flex justify-between items-center h-full mx-auto">
          <div className="flex items-center">
            <Link
              to="/"
              className="flex items-center group transition-transform duration-300 hover:scale-105"
            >
              <img
                src="/logo-gold.png"
                alt="DayShift Sports MMA"
                className="h-10 md:h-10 object-contain"
              />
            </Link>
          </div>

          <nav className="hidden md:flex items-center space-x-6 h-full">
            <Link
              to="/home"
              className={`px-2 h-full flex items-center transition-all duration-200 text-sm font-medium relative ${
                isActive("/home") ? "text-gold" : "text-white hover:text-gold"
              }`}
            >
              <span>Home</span>
              {isActive("/home") && (
                <span className="absolute bottom-1 left-0 w-full h-0.5 bg-gold rounded-full"></span>
              )}
            </Link>

            <Link
              to="/leagues"
              className={`px-2 h-full flex items-center transition-all duration-200 text-sm font-medium relative ${
                isActive("/leagues")
                  ? "text-gold"
                  : "text-white hover:text-gold"
              }`}
            >
              <span>Leagues</span>
              {isActive("/leagues") && (
                <span className="absolute bottom-1 left-0 w-full h-0.5 bg-gold rounded-full"></span>
              )}
            </Link>

            <Link
              to="/events"
              className={`px-2 h-full flex items-center transition-all duration-200 text-sm font-medium relative ${
                isActive("/events") ? "text-gold" : "text-white hover:text-gold"
              }`}
            >
              <span>Events</span>
              {isActive("/events") && (
                <span className="absolute bottom-1 left-0 w-full h-0.5 bg-gold rounded-full"></span>
              )}
            </Link>

            <Link
              to="/globalcompetition"
              className={`px-2 h-full flex items-center transition-all duration-200 text-sm font-medium relative ${
                isActive("/globalcompetition")
                  ? "text-gold"
                  : "text-white hover:text-gold"
              }`}
            >
              <span>Global</span>
              {isActive("/globalcompetition") && (
                <span className="absolute bottom-1 left-0 w-full h-0.5 bg-gold rounded-full"></span>
              )}
            </Link>

            {user && user.role === "admin" && (
              <Link
                to="/admin"
                className={`px-2 h-full flex items-center transition-all duration-200 text-sm font-medium relative ${
                  isActive("/admin")
                    ? "text-gold"
                    : "text-white hover:text-gold"
                }`}
              >
                <span>Admin</span>
                {isActive("/admin") && (
                  <span className="absolute bottom-1 left-0 w-full h-0.5 bg-gold rounded-full"></span>
                )}
              </Link>
            )}

            {/* Search Bar - Desktop */}
            <div className="flex items-center">
              <SearchBar className="mr-4" />
            </div>

            {/* System health (admin) + Notification Bell */}
            <div className="flex items-center">
              {user && user.role === "admin" && <HealthIndicator />}
              <NotificationBell
                className="mr-3"
                position="right"
                showDropdown={true}
              />
            </div>

            <div className="relative ml-3" ref={dropdownRef}>
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center space-x-1.5 focus:outline-none bg-white/10 hover:bg-white/20 rounded-full pl-1 pr-2 py-1 transition-all duration-200 min-h-[36px]"
              >
                <div className="w-7 h-7 rounded-full overflow-hidden">
                  {user && user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.username || "User"}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-white flex items-center justify-center">
                      <img
                        src="/emblem-gold.png"
                        alt="DayShift Emblem"
                        className="w-4 h-4 object-contain"
                      />
                    </div>
                  )}
                </div>
                <span className="text-xs text-white hidden sm:block">
                  {user?.username || "User"}
                </span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`text-xs text-white transition-transform duration-200 ${
                    profileOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-dark-grey rounded-lg shadow-xl py-1 z-50 border border-dark-grey/30 animate-fadeIn">
                  <Link
                    to="/profile"
                    className="flex items-center px-3 py-2.5 text-sm font-medium text-white hover:text-gold transition-colors min-h-[44px]"
                    onClick={() => setProfileOpen(false)}
                  >
                    <FontAwesomeIcon
                      icon={faUser}
                      className="mr-2 text-gold w-4"
                    />
                    Profile
                  </Link>
                  <Link
                    to="/rules"
                    className="flex items-center px-3 py-2.5 text-sm font-medium text-white hover:text-gold transition-colors min-h-[44px]"
                    onClick={() => setProfileOpen(false)}
                  >
                    <FontAwesomeIcon
                      icon={faQuestionCircle}
                      className="mr-2 text-gold w-4"
                    />
                    Rules
                  </Link>
                  <div className="border-t border-white/10 my-1"></div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full text-left px-3 py-2.5 text-sm font-medium text-white hover:text-deep-red transition-colors min-h-[44px]"
                  >
                    <FontAwesomeIcon
                      icon={faSignOutAlt}
                      className="mr-2 text-gold w-4"
                    />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </nav>

          {/* Mobile search icon, notification bell and profile avatar menu */}
          <div className="md:hidden flex items-center space-x-2">
            <button
              className="text-white focus:outline-none hover:text-gold p-2.5 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              onClick={() => setSearchModalOpen(true)}
              aria-label="Search users"
            >
              <FontAwesomeIcon icon={faSearch} className="w-5 h-5" />
            </button>
            {user && user.role === "admin" && <HealthIndicator />}
            <NotificationBell
              className="notification-bell-mobile"
              position="left"
              showDropdown={true}
            />
            <button
              className="focus:outline-none rounded-full transition-all duration-200 min-h-[44px] min-w-[44px] flex items-center justify-center"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
            >
              <div
                className={`w-8 h-8 rounded-full overflow-hidden ring-2 transition-all duration-200 ${menuOpen ? "ring-gold" : "ring-white/30 hover:ring-white/60"}`}
              >
                {user && user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.username || "User"}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-white flex items-center justify-center">
                    <img
                      src="/emblem-gold.png"
                      alt="DayShift Emblem"
                      className="w-4 h-4 object-contain"
                    />
                  </div>
                )}
              </div>
            </button>
          </div>
        </div>

        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            menuOpen ? "max-h-[500px] opacity-100 pb-3" : "max-h-0 opacity-0"
          }`}
        >
          <div className="mt-2 rounded-lg bg-dark-grey/95 backdrop-blur-sm shadow-xl border border-white/10">
            <Link
              to="/home"
              className={`flex items-center py-3 px-4 transition-colors min-h-[48px] ${
                isActive("/home")
                  ? "text-gold font-medium bg-white/5"
                  : "text-white hover:text-gold hover:bg-white/5"
              }`}
              onClick={() => setMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/leagues"
              className={`flex items-center py-3 px-4 transition-colors min-h-[48px] ${
                isActive("/leagues")
                  ? "text-gold font-medium bg-white/5"
                  : "text-white hover:text-gold hover:bg-white/5"
              }`}
              onClick={() => setMenuOpen(false)}
            >
              Leagues
            </Link>
            <Link
              to="/events"
              className={`flex items-center py-3 px-4 transition-colors min-h-[48px] ${
                isActive("/events")
                  ? "text-gold font-medium bg-white/5"
                  : "text-white hover:text-gold hover:bg-white/5"
              }`}
              onClick={() => setMenuOpen(false)}
            >
              Events
            </Link>
            <Link
              to="/globalcompetition"
              className={`flex items-center py-3 px-4 transition-colors min-h-[48px] ${
                isActive("/globalcompetition")
                  ? "text-gold font-medium bg-white/5"
                  : "text-white hover:text-gold hover:bg-white/5"
              }`}
              onClick={() => setMenuOpen(false)}
            >
              Global Competition
            </Link>
            {user && user.role === "admin" && (
              <Link
                to="/admin"
                className={`flex items-center py-3 px-4 transition-colors min-h-[48px] ${
                  isActive("/admin")
                    ? "text-gold font-medium bg-white/5"
                    : "text-white hover:text-gold hover:bg-white/5"
                }`}
                onClick={() => setMenuOpen(false)}
              >
                Admin
              </Link>
            )}
            <div className="border-t border-white/10 my-1"></div>
            <Link
              to="/profile"
              className={`flex items-center py-3 px-4 transition-colors min-h-[48px] ${
                isActive("/profile")
                  ? "text-gold font-medium bg-white/5"
                  : "text-white hover:text-gold hover:bg-white/5"
              }`}
              onClick={() => setMenuOpen(false)}
            >
              <FontAwesomeIcon icon={faUser} className="mr-2 text-gold w-4" />
              Profile
            </Link>
            <Link
              to="/rules"
              className={`flex items-center py-3 px-4 transition-colors min-h-[48px] ${
                isActive("/rules")
                  ? "text-gold font-medium bg-white/5"
                  : "text-white hover:text-gold hover:bg-white/5"
              }`}
              onClick={() => setMenuOpen(false)}
            >
              <FontAwesomeIcon
                icon={faQuestionCircle}
                className="mr-2 text-gold w-4"
              />
              Rules
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center w-full text-left py-3 px-4 text-white hover:text-deep-red hover:bg-white/5 transition-colors min-h-[48px]"
            >
              <FontAwesomeIcon
                icon={faSignOutAlt}
                className="mr-2 text-gold w-4"
              />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Search Modal */}
      <UserSearchModal
        isOpen={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
      />
    </header>
  );
};

export default Header;
